import { useRouter } from 'vue-router'
import * as HELPERS from '@/utils/helpers'
import labels from '@/translations/labels.json'
import CONSTANTS from '@/constants/Config.constants'
import { IArtifactService } from '../IArtifactService'
import { useRouteStore } from '@/stores/routeStore'
import { useProjectStore } from '@/stores/projectStore'
import { useSnapshotStore } from '@/stores/snapshotStore'
import { useTraceDataStore } from '@/stores/traceDataStore'
import { IProjectService } from '../IProjectService'
import { ISnapshotService } from '../ISnapshotService'
import { IGlobalFilterService } from '../IGlobalFilterService'
import { useGlobalFilterStore } from '@/stores/globalFilterStore'
import { asSnapshotReference } from '@/models'

enum SYNC_REDIRECT_TARGET {
  PROJECT_LIST,
  EXPLORER,
  OVERVIEW,
}

// https://gitlab.com/itemis/solutions/itemis-analyze/web/analyze-frontend/-/issues/634
export default class SyncStoreAndRouteService {
  private validityMap = new Map<string, boolean>()
  private redirectTarget: SYNC_REDIRECT_TARGET

  constructor(
    private projectService: IProjectService,
    private snapshotService: ISnapshotService,
    private artifactService: IArtifactService,
    private globalFilterService: IGlobalFilterService,
  ) {}

  async keepStoreAndRouteInSync(): Promise<void> {
    if (await this.checkIfRouteContainsValidProjectId()) {
      await this.checkIfRouteParamsAreValid().then((response) => {
        this.updateStoreByRoute(response)
      })
    } else {
      this.redirectToNewTarget()
    }
  }

  async checkIfRouteContainsValidProjectId(): Promise<boolean> {
    const routeStore = useRouteStore()
    const projectStore = useProjectStore()
    // the route should always contain a projectId, we can assume the route invalid if there is no projectId
    if (routeStore.projectId !== undefined && (await this.checkIfProjectIdisValid())) {
      projectStore.setCurrentProject(
        await this.projectService.getProjectById(parseInt(routeStore.projectId)),
      )
      return true
    } else {
      return false
    }
  }

  async checkIfRouteParamsAreValid(): Promise<boolean> {
    const routeStore = useRouteStore()

    const validityChecks: Promise<boolean>[] = []

    if (routeStore.snapshotId !== undefined) {
      const isSnapshotIdValid = this.checkIfSnapshotIdisValid()
      validityChecks.push(isSnapshotIdValid)
      this.validityMap.set('snapshotId', await isSnapshotIdValid)
    }

    if (routeStore.snapshotId !== undefined && routeStore.artifactId !== undefined) {
      const isArtifactIdValid = this.checkIfArtifactIdisValid()
      validityChecks.push(isArtifactIdValid)
      this.validityMap.set('artifactId', await isArtifactIdValid)
    }

    if (routeStore.filterId !== undefined) {
      const isFilterIdValid = this.checkIfFilterIdIsValid()
      validityChecks.push(isFilterIdValid)
      this.validityMap.set('filterId', await isFilterIdValid)
    }

    const asyncEvery = async (
      validityChecks: Promise<boolean>[],
      predicate: { (promise: Promise<boolean>): Promise<boolean> },
    ) => {
      for (const promise of validityChecks) {
        if (!(await predicate(promise))) return false
      }
      return true
    }
    return await asyncEvery(validityChecks, async (promise: Promise<boolean>) => {
      return await promise
    })
  }

  async checkIfProjectIdisValid(): Promise<boolean> {
    const routeStore = useRouteStore()
    let response: boolean
    try {
      return await this.projectService
        .getProjectById(parseInt(HELPERS.requireDefined(routeStore.projectId)))
        .then((promise) => {
          response = promise !== undefined
          return response
        })
    } catch {
      this.alertIfResponseIsInvalid(
        false,
        labels.ALERT_VIEW.INVALID_PROJECT_ID,
        HELPERS.requireDefined(routeStore.projectId),
      )
      this.redirectTarget = SYNC_REDIRECT_TARGET.PROJECT_LIST
    }
    return false
  }

  async checkIfSnapshotIdisValid(): Promise<boolean> {
    const routeStore = useRouteStore()

    try {
      const snapshot = await this.snapshotService.getSnapshot(
        parseInt(HELPERS.requireDefined(routeStore.projectId)),
        asSnapshotReference(HELPERS.requireDefined(routeStore.snapshotId)),
      )

      if (
        snapshot.data.id.toString() === routeStore.snapshotId ||
        routeStore.snapshotId == 'latest'
      ) {
        return true // Valid snapshot ID
      } else {
        throw new Error('Invalid snapshot ID')
      }
    } catch {
      this.alertIfResponseIsInvalid(
        false,
        labels.ALERT_VIEW.INVALID_SNAPSHOT_ID,
        HELPERS.requireDefined(routeStore.snapshotId),
      )
      this.redirectTarget = SYNC_REDIRECT_TARGET.OVERVIEW
      return false
    }
  }

  async checkIfArtifactIdisValid(): Promise<boolean> {
    const routeStore = useRouteStore()
    try {
      return await this.artifactService
        .getArtifact(
          parseInt(HELPERS.requireDefined(routeStore.projectId)),
          asSnapshotReference(HELPERS.requireDefined(routeStore.snapshotId)),
          parseInt(HELPERS.requireDefined(routeStore.artifactId)),
          HELPERS.parseIntUndefined(routeStore.filterId),
        )
        .then((artifact) => artifact !== undefined)
    } catch {
      this.alertIfResponseIsInvalid(
        false,
        labels.ALERT_VIEW.INVALID_ARTIFACT_ID,
        HELPERS.requireDefined(routeStore.artifactId),
      )
      this.redirectTarget = SYNC_REDIRECT_TARGET.EXPLORER
      return false
    }
  }

  async checkIfFilterIdIsValid(): Promise<boolean> {
    const routeStore = useRouteStore()
    return await this.globalFilterService
      .getGlobalFilters(parseInt(HELPERS.requireDefined(routeStore.projectId)))
      .then((filters) => {
        const response = filters.some((filter) => filter.id.toString() === routeStore.filterId)

        this.alertIfResponseIsInvalid(
          response,
          labels.ALERT_VIEW.INVALID_FILTER_ID,
          HELPERS.requireDefined(routeStore.filterId),
        )

        if (!response) {
          this.redirectTarget = SYNC_REDIRECT_TARGET.OVERVIEW
        }

        return response
      })
  }

  alertIfResponseIsInvalid(requestResult: boolean, label: string, error: string): void {
    if (!requestResult) {
      const message = HELPERS.replaceVariableInString(label, error)
      HELPERS.showTopAlertMessage(true, 'error', message)
    }
  }

  async checkForSnapshots(): Promise<boolean> {
    const routeStore = useRouteStore()
    const snapshotStore = useSnapshotStore()
    return await this.snapshotService
      .getSnapshots(parseInt(HELPERS.requireDefined(routeStore.projectId)))
      .then((promise) => {
        const response = Object.entries(promise.data).length > 0
        if (!response) {
          snapshotStore.setCurrentSnapshot(undefined)
        }
        return response
      })
  }

  async updateStoreByRoute(followRoute: boolean): Promise<void> {
    const routeStore = useRouteStore()
    const snapshotStore = useSnapshotStore()
    const traceDataStore = useTraceDataStore()
    let snapshotsLoaded = false

    for (const [key, isValid] of this.validityMap.entries()) {
      if (key === 'snapshotId' && isValid) {
        await snapshotStore.loadSnapshotById(
          parseInt(HELPERS.requireDefined(routeStore.projectId)),
          asSnapshotReference(HELPERS.requireDefined(routeStore.snapshotId)),
        )
        snapshotsLoaded = true
      }

      if (key === 'artifactId' && isValid) {
        traceDataStore.setArtifactId(HELPERS.requireDefined(routeStore.artifactId))
      }

      if (key === 'filterId' && isValid) {
        useGlobalFilterStore().loadGlobalFiltersFromApi(
          parseInt(HELPERS.requireDefined(routeStore.projectId)),
          parseInt(HELPERS.requireDefined(routeStore.filterId)),
        )

        followRoute = true
      }
    }

    if (
      !snapshotsLoaded &&
      routeStore.snapshotId === undefined &&
      !/^\/project\/\d+\/snapshots$/.test(routeStore.path || '')
    ) {
      // If no specific snapshot is targeted, or no valid snapshot was loaded, handle accordingly
      followRoute = await this.loadSnapshots()
    }

    if (!followRoute) {
      this.redirectToNewTarget()
    }
  }

  async loadSnapshots(): Promise<boolean> {
    const routeStore = useRouteStore()
    const snapshotStore = useSnapshotStore()
    if (routeStore.snapshotId === undefined && (await this.checkForSnapshots())) {
      await snapshotStore.loadSnapshotsFromApi({
        projectId: parseInt(HELPERS.requireDefined(routeStore.projectId)),
      })
      return true
    } else if (
      !HELPERS.requireDefined(routeStore.path).match('overview') &&
      !(await this.checkForSnapshots())
    ) {
      // Prevent redirecting away from the snapshots managment view
      if (/^\/project\/\d+\/snapshots$/.test(routeStore.path || '')) {
        return true
      }
      this.redirectTarget = SYNC_REDIRECT_TARGET.OVERVIEW
      HELPERS.showTopAlertMessage(true, 'error', labels.ALERT_VIEW.NO_SNAPSHOTS_FOUND)
      return false
    } else {
      HELPERS.showTopAlertMessage(true, 'error', labels.ALERT_VIEW.NO_SNAPSHOTS_FOUND)
      return true
    }
  }

  async redirectToNewTarget(): Promise<void> {
    const routeStore = useRouteStore()
    const snapshotStore = useSnapshotStore()

    let target: string
    switch (this.redirectTarget) {
      case SYNC_REDIRECT_TARGET.PROJECT_LIST:
        target = labels.SIDEBAR.MAIN.PROJECT_LIST
        break
      case SYNC_REDIRECT_TARGET.EXPLORER:
        target = labels.SIDEBAR.MAIN.EXPLORER
        await snapshotStore.loadSnapshotsFromApi({
          projectId: parseInt(HELPERS.requireDefined(routeStore.projectId)),
        })
        break
      case SYNC_REDIRECT_TARGET.OVERVIEW:
        target = labels.SIDEBAR.MAIN.PROJECT_OVERVIEW
        break
      default:
        target = labels.SIDEBAR.MAIN.PROJECT_LIST
    }
    if (
      !(
        target === labels.SIDEBAR.MAIN.PROJECT_LIST &&
        routeStore.path === CONSTANTS.BASE_ROUTES.PROJECTLIST
      )
    ) {
      useRouter().push({ name: target })
    }
  }
}
